import { useCallback, useMemo } from 'react';

const specialCharsRegex = /[-_'`]/g;

const normalize = (text: string) =>
  text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(specialCharsRegex, '')
    .toLowerCase();

/**
 *
 * @param haystack array of objects to search throught
 * @param matchOn the key we want to perform the search on. Currently only supports one key
 * @returns a function to perform a search
 *
 * Performance can be improved by using a reverse index. This would be overkill for most use cases, but can be applied for schools or scorable items in the results screen.
 */
export function useFuzzySearch<T extends Object, K extends Extract<keyof T, string>>(
  haystack: T[],
  matchOn: K,
): (needle: string) => T[] {
  const normalizedHaystack = useMemo(() => {
    return haystack.map(item => {
      if (typeof item[matchOn] !== 'string') {
        throw new Error(`Expected ${matchOn} to be a string, but got ${typeof item[matchOn]}`);
      }

      return {
        ...item,
        textWithoutSpecialChars: item[matchOn].replace(specialCharsRegex, '').toLowerCase(),
        normalizedText: normalize(item[matchOn]),
      };
    });
  }, [haystack, matchOn]);

  const search = useCallback(
    (needle: string): T[] => {
      const needleHasAccents = needle !== normalize(needle.replace(specialCharsRegex, '').toLowerCase());

      if (needleHasAccents) {
        return normalizedHaystack.filter(h =>
          h.textWithoutSpecialChars.includes(needle.replace(specialCharsRegex, '').toLowerCase()),
        ) as unknown as T[];
      }

      const normalizedNeedle = normalize(needle);
      return normalizedHaystack.filter(h => h.normalizedText.includes(normalizedNeedle)) as unknown as T[];
    },
    [normalizedHaystack],
  );

  return search;
}

const stripSpecialChars = (text: string) => text.replace(specialCharsRegex, '').toLowerCase();

export function fuzzyMatch(text: string, needle: string): boolean {
  const needleHasAccents = needle !== normalize(stripSpecialChars(needle));

  if (needleHasAccents) {
    return stripSpecialChars(text).includes(stripSpecialChars(needle));
  }

  return normalize(text).includes(normalize(needle));
}
